import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
/**
 * The cook log (owner ask 2026-08-18: *"I want to have a 'Cooked this' thing we can do with
 * recipes that also add to the timeline (I made smoked salmon today!)"*).
 *
 * One entry per time somebody actually cooked something. It lives in its own family doc
 * rather than as a field on `Recipe` for two reasons: a cook is an *event* with its own
 * instant and its own author, and the check-in feed has to be able to read cooks without
 * opening (and therefore subscribing to) the whole recipe box.
 *
 * **No tallies, ever.** The recipe detail shows "Last cooked 3 days ago by Dan" and nothing
 * else — no "cooked 14 times", no "your most-cooked recipe", no per-member ranking (owner
 * call 2026-08-18; memory/no-demand-mechanics.md, and the same rule the games archive states
 * in `apps/web/src/modules/games/archive.ts`). That is why the only aggregate helper in this
 * file is `lastCookOf`, which returns *an entry*, not a number. Each cook is its own memory:
 * a Tuesday somebody made salmon. If the owner ever wants a count he will ask for one; the
 * data sitting right here is not permission to render it.
 *
 * Split of responsibilities, same as every other collaborative surface (ROADMAP §1.3): Yjs
 * owns the entries, the client writes them directly, and no REST endpoint mutates them.
 */
/** Yjs doc holding `Y.Map<cookId, CookLogEntry>` — family-visible, like the games registry. */
export const COOKS_DOC = 'cooks';
/** Key of the `Y.Map` inside `COOKS_DOC`. */
export const COOKS_MAP_KEY = 'cooks';
/**
 * Hard cap on a cook's optional note. Deliberately the same 140 as `CHECKIN_TEXT_MAX` —
 * a note here is the same kind of thing as a check-in's one-liner ("used the good maple
 * this time"), and giving it a different ceiling would only invite it to grow into a
 * second, longer place to write about a recipe when `Recipe.notes` already exists.
 */
export const COOK_NOTE_MAX = 140;
export const cookLogEntrySchema = z.object({
    id: idSchema,
    recipeId: idSchema,
    /**
     * The recipe's title **denormalized at cook time**, on purpose.
     *
     * A cook is a memory of a night, and the timeline has to keep saying "Dan cooked Smoked
     * salmon" in six months when the recipe has been deleted, renamed to "Salmon, 2nd
     * attempt", or replaced wholesale. Resolving the title through `recipeId` at render time
     * would make a deleted recipe silently blank out real history — the one outcome the feed
     * must never produce. `recipeId` is kept alongside it purely as a *link target*: the card
     * is tappable when the recipe still exists and merely inert when it doesn't, which is a
     * far smaller failure than a memory that erases itself.
     */
    recipeTitle: z.string().trim().min(1).max(300),
    cookedBy: idSchema,
    cookedAt: isoDateTimeSchema,
    note: z.string().trim().max(COOK_NOTE_MAX).optional(),
});
/**
 * Path to a recipe in the web app. Introduced here because the cook log is the first thing
 * *outside* the recipes module that needs to link to one (the check-in feed's cook card) —
 * same role `gamePostPath` plays for the games registry. Always a same-origin path, never
 * an absolute URL (north-star #7).
 */
export function recipePath(recipeId) {
    return `/recipes/${recipeId}`;
}
/** Newest cook first; ties broken by id so nothing shuffles between renders — the same
 * stable-sort rule the check-in feed and the games archive both use. */
export function sortCooksNewestFirst(cooks) {
    return [...cooks].sort((a, b) => {
        const diff = Date.parse(b.cookedAt) - Date.parse(a.cookedAt);
        return diff !== 0 ? diff : b.id.localeCompare(a.id);
    });
}
/**
 * The most recent cook of one recipe, or `undefined` if it has never been cooked.
 *
 * The *only* thing the recipe detail page is allowed to know about its cook history, and
 * that is the point — this returns an entry so the UI can say "Last cooked 3 days ago by
 * Dan", and there is deliberately no sibling that returns how many there were. `undefined`
 * means the caller renders nothing at all: a recipe nobody has cooked yet gets no empty
 * state, because "you haven't cooked this" is a nag wearing a placeholder's clothes.
 */
export function lastCookOf(cooks, recipeId) {
    let latest;
    for (const cook of cooks) {
        if (cook.recipeId !== recipeId)
            continue;
        if (!latest) {
            latest = cook;
            continue;
        }
        const diff = Date.parse(cook.cookedAt) - Date.parse(latest.cookedAt);
        if (diff > 0 || (diff === 0 && cook.id.localeCompare(latest.id) > 0))
            latest = cook;
    }
    return latest;
}
const MS_PER_DAY = 86_400_000;
/** Midnight-local for a date, so "last night" and "this morning" land a whole day apart
 * regardless of the clock times involved. */
function localDayStart(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}
/**
 * "today" / "yesterday" / "3 days ago" / "2 weeks ago" — the tail of "Last cooked ___ by
 * Dan" (the owner's own phrasing, 2026-08-18).
 *
 * Whole *calendar* days in local time, not elapsed hours: something cooked at 9pm last
 * night reads "yesterday" at 8am, which is what a person means. Nothing finer than a day is
 * offered, because a cook is a thing that happened on an evening, not at a timestamp — the
 * feed card already carries the clock (`formatClock`) for the one place it matters.
 *
 * Lives in shared rather than as yet another local `timeAgo` (there are four of those under
 * `apps/web/src/modules/`) because two surfaces in two different modules render this exact
 * string off the same field, and they must not drift. Those four stay where they are: they
 * are minute-granular "2m ago" edit stamps, a different unit and a different question.
 */
export function cookedAgoLabel(iso, now = new Date()) {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime()))
        return 'at some point';
    const days = Math.round((localDayStart(now) - localDayStart(then)) / MS_PER_DAY);
    // A clock-skewed device can hand us a cook stamped in the future; "today" is the honest
    // reading of that, and it beats rendering "-1 days ago".
    if (days <= 0)
        return 'today';
    if (days === 1)
        return 'yesterday';
    if (days < 7)
        return `${days} days ago`;
    if (days < 28)
        return plural(Math.floor(days / 7), 'week');
    if (days < 365)
        return plural(Math.max(1, Math.floor(days / 30)), 'month');
    return plural(Math.floor(days / 365), 'year');
}
function plural(count, unit) {
    return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
}
//# sourceMappingURL=cooks.js.map